import React, { useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicShell";
import { toast } from "sonner";
import { Envelope, Download, CheckCircle, Lock } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function DownloadReceipts() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [donations, setDonations] = useState([]);

  const requestOtp = async () => {
    if (!email.trim() || !email.includes("@")) return toast.error("Please enter a valid email");
    setLoading(true);
    try {
      await api.post("/receipts/request-otp", { email });
      toast.success("OTP sent to your email");
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send OTP"); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (!otp || otp.length < 4) return toast.error("Enter the OTP");
    setLoading(true);
    try {
      const { data } = await api.post("/receipts/verify-otp", { email, otp });
      setToken(data.token);
      setDonations(data.donations || []);
      setStep(3);
      if (!data.donations?.length) toast.info("No successful donations found for this email");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid or expired OTP"); }
    finally { setLoading(false); }
  };

  const download = (id, type) => {
    const url = `${BACKEND}/api/receipts/${id}/pdf?token=${encodeURIComponent(token)}&type=${type}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <PublicHeader />
      <section className="max-w-xl mx-auto px-5 md:px-10 py-12 md:py-20">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#EEF7EA] grid place-items-center mx-auto mb-4">
            <Download size={28} weight="fill" className="text-[#4FAC39]" />
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Download Your Receipts</h1>
          <p className="text-[#56624F] mt-2">Enter the email you used to donate. We&rsquo;ll send a one-time code to verify.</p>
        </div>

        {step === 1 && (
          <div className="seva-card space-y-4" data-testid="receipts-step-1">
            <label className="block">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Envelope size={16} /> Your email</div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white" data-testid="receipts-email-input" />
            </label>
            <button onClick={requestOtp} disabled={loading} className="btn-primary w-full" data-testid="receipts-request-otp">{loading ? "Sending..." : "Send OTP"}</button>
          </div>
        )}

        {step === 2 && (
          <div className="seva-card space-y-4" data-testid="receipts-step-2">
            <div className="text-sm text-[#56624F]">We sent a 6-digit code to <strong>{email}</strong></div>
            <label className="block">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Lock size={16} /> One-time code</div>
              <input maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white text-center text-2xl tracking-[0.5em] font-mono" data-testid="receipts-otp-input" />
            </label>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="btn-secondary flex-1">Back</button>
              <button onClick={verify} disabled={loading} className="btn-primary flex-1" data-testid="receipts-verify-otp">{loading ? "Verifying..." : "Verify"}</button>
            </div>
            <button onClick={requestOtp} className="text-sm text-[#4FAC39] hover:underline w-full text-center">Didn&rsquo;t get it? Resend code</button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="receipts-step-3">
            <div className="flex items-center gap-2 text-[#2F7A4A] bg-[#E6F3DC] border border-[#4FAC39]/30 rounded-xl px-4 py-3 text-sm">
              <CheckCircle size={20} weight="fill" /> Verified &middot; showing {donations.length} donation{donations.length === 1 ? "" : "s"} for {email}
            </div>
            {donations.map((d) => (
              <div key={d.id} className="seva-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`receipt-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{d.campaign_title}</div>
                  <div className="text-xs text-[#56624F]">{(d.completed_at || d.created_at || "").slice(0, 10)} &middot; Receipt {d.id.slice(0, 8).toUpperCase()}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-display font-bold">{formatINR(d.amount)}</div>
                  <button onClick={() => download(d.id, "donation")} className="btn-secondary !py-2 !px-3 text-xs" data-testid={`download-receipt-${d.id}`}>
                    <Download size={14} className="inline mr-1" /> Receipt
                  </button>
                  {d.claim_80g && (
                    <button onClick={() => download(d.id, "80g")} className="btn-primary !py-2 !px-3 text-xs" data-testid={`download-80g-${d.id}`}>
                      <Download size={14} className="inline mr-1" /> 80G
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!donations.length && (
              <div className="seva-card text-center text-[#56624F]">No successful donations found for this email.</div>
            )}
          </div>
        )}
      </section>
      <PublicFooter />
    </>
  );
}
